import moment, { Moment } from 'moment/moment'
import cn from 'classnames'
import { useCallback, useEffect, useState } from 'react'
import { motion } from 'framer-motion'

const getWeeksInMonth = (year: number, month: number): Moment[][] => {
  const firstDayOfMonth: Moment = moment()
    .year(year)
    .month(month)
    .startOf('month')
  const lastDayOfMonth: Moment = moment().year(year).month(month).endOf('month')
  const weeks: Moment[][] = []

  let currentWeek: Moment[] = []
  const currentDay: Moment = firstDayOfMonth.clone().startOf('week')

  while (currentDay.isSameOrBefore(lastDayOfMonth, 'day')) {
    currentWeek.push(currentDay.clone())

    if (currentDay.weekday() === 6) {
      weeks.push(currentWeek)
      currentWeek = []
    }
    currentDay.add(1, 'day')
  }

  if (currentWeek.length > 0) {
    weeks.push(currentWeek)
  }

  const lastWeekOfMonth = [...weeks[weeks.length - 1]]
  const daysToFillWeek = 7 - lastWeekOfMonth.length

  // Fill in missing days of the calendar week
  if (daysToFillWeek) {
    for (let i = 0; i < daysToFillWeek; i++) {
      lastWeekOfMonth.push(
        lastWeekOfMonth[lastWeekOfMonth.length - 1].clone().add(1, 'day')
      )
    }
    weeks[weeks.length - 1] = lastWeekOfMonth
  }
  return weeks
}

const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
  <div
    key={day}
    className="w-full h-8 flex items-center justify-start pl-1 text-dark-3 text-sm"
  >
    {day}
  </div>
))

interface CalendarProps {
  date: Moment
  value?: Moment | null | undefined
  onChange?: (date: Moment) => void
  startDate?: Moment | null
  endDate?: Moment | null
  onChangeRange?: (dates: (Moment | null)[], shouldUpdate: boolean) => void
  isRange?: boolean
  disablePast?: boolean
  disableBefore?: Moment | null
  disableAfter?: Moment | null
}

const Calendar = ({
  date,
  onChange,
  value,
  startDate,
  endDate,
  onChangeRange,
  isRange,
  disablePast,
  disableBefore,
  disableAfter
}: CalendarProps) => {
  const weeksInMonth: Moment[][] = getWeeksInMonth(date.year(), date.month())
  const [step, setStep] = useState<'start' | 'end'>('start')

  const isDaySelected = useCallback(
    (day: Moment): boolean => {
      if (isRange) {
        return (
          moment(startDate as Moment).isSame(day, 'd') ||
          moment(endDate as Moment).isSame(day, 'd')
        )
      }

      if (value === null || value === undefined) {
        return false
      }

      if (moment(value as Moment).isValid()) {
        return moment(value).isSame(day, 'd')
      }

      return false
    },
    [endDate, isRange, startDate, value]
  )
  useEffect(() => {
    if (isRange && !startDate) {
      return setStep('start')
    }

    if (isRange && startDate && endDate) {
      return setStep('start')
    }

    if (isRange && startDate && !endDate) {
      return setStep('end')
    }

    if (!isRange) setStep('start')
  }, [endDate, isRange, startDate])

  const checkDisabled = useCallback(
    (day: Moment) => {
      if (isRange) {
        return step === 'end' || disablePast ? day.isBefore(startDate) : false
      }
      const shouldDisableBefore = disableBefore
        ? day.isBefore(disableBefore)
        : false
      const shouldDisableAfter = disableAfter
        ? day.isAfter(disableAfter)
        : false

      return shouldDisableBefore || shouldDisableAfter
    },
    [isRange, step, disablePast, startDate, disableBefore, disableAfter]
  )

  return (
    <div className="w-[180px] m-auto">
      <div className="flex flex-row gap-2">{weekDays}</div>
      {weeksInMonth.map((week: Moment[], index: number) => {
        return (
          <div key={index} className="flex flex-row">
            {week.map((day: Moment) => {
              const isToday = moment().isSame(day, 'day')
              const isDiffMonth = !moment(date).isSame(day, 'month')
              const isSelected = isDaySelected(day)
              const isDisabled = checkDisabled(day)

              const isBetween =
                startDate?.isValid() &&
                endDate?.isValid() &&
                moment(day).isBetween(startDate, endDate, 'days', '[]')

              const isLeftEdge =
                day.day() === 0 || startDate?.isSame(day, 'day')
              const isRightEdge = day.day() === 6 || endDate?.isSame(day, 'day')

              return (
                <div
                  key={day.format('YYYY-MM-DD')}
                  className="flex w-full items-center justify-around gap-1"
                >
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className={cn([
                      'w-full h-7 select-none flex items-center justify-around text-sm  border border-transparent rounded-md cursor-pointer hover:bg-dark-1 my-1',
                      isToday && 'bg-dark-1',
                      isDiffMonth && 'text-dark-2',
                      isDisabled &&
                        'text-dark-2 hover:!bg-white !cursor-auto border border-transparent',
                      (isSelected || isBetween) && 'border-primary',
                      isBetween && '!border-x-0 rounded-none',
                      isBetween && isLeftEdge && '!border-l rounded-l-md ',
                      isRightEdge && isRightEdge && '!border-r rounded-r-md '
                    ])}
                    onClick={() => {
                      if (isDisabled) return
                      if (isRange) {
                        if (step === 'start') {
                          onChangeRange?.([day, null], false)
                          setStep('end')
                        }
                        if (step === 'end') {
                          onChangeRange?.([startDate as Moment, day], true)
                          setStep('start')
                        }
                      }

                      onChange?.(day.clone())
                    }}
                  >
                    {day.format('D')}
                  </motion.div>
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

export default Calendar
